import React from "react";
import styled from "styled-components";
import SiteButton from "./SiteButton";
import { revealProps } from "./Reveal";
import { CTA, FEATURED_TESTIMONIAL } from "./site.data";
import { IconQuote } from "./SiteIcons";
import { C, MQ, TYPE } from "./WebsiteTheme.style";

const TestimonialWrapper = styled.section`
	/* Directly after a dark band: space above only, the next section brings its own */
	&.st_after_band {
		padding-top: 64px;

		${MQ.PHONE} {
			padding-top: 48px;
		}
	}

	.st_card {
		display: grid;
		grid-template-columns: auto minmax(0, 1fr) auto;
		align-items: center;
		gap: 32px;
		padding: 32px 40px;
		border: 1px solid ${C.creamLine};
		border-radius: 20px;
		background: ${C.cream};

		${MQ.STACK} {
			grid-template-columns: auto minmax(0, 1fr);
			gap: 24px;
			padding: 28px;
		}

		${MQ.PHONE} {
			grid-template-columns: 1fr;
			gap: 20px;
			padding: 24px 20px;
		}
	}

	/* Initials avatar (no stock face stands in for a real teacher) */
	.st_avatar {
		position: relative;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 96px;
		height: 96px;
		border: 4px solid #fff;
		border-radius: 50%;
		background: linear-gradient(135deg, ${C.blue} 0%, ${C.navy} 100%);
		box-shadow: ${C.cardShadow};
		color: #fff;
		font-size: 30px;
		font-weight: 700;
		letter-spacing: 1px;

		${MQ.PHONE} {
			width: 72px;
			height: 72px;
			font-size: 24px;

			.st_mark {
				right: -12px;
				width: 30px;
				height: 30px;
			}
		}
	}

	.st_mark {
		position: absolute;
		right: -6px;
		bottom: -2px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 34px;
		height: 34px;
		border: 3px solid #fff;
		border-radius: 50%;
		background: ${C.gold};
		color: ${C.navy};

		svg {
			width: 16px;
			height: 16px;
		}
	}

	.st_body {
		display: flex;
		flex-direction: column;
		gap: 14px;
	}

	.st_quote {
		color: ${C.navy};
		${TYPE.quote}
	}

	.st_author {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px 12px;
		color: ${C.muted};
		${TYPE.caption}

		strong {
			color: ${C.blue};
			font-size: 16px;
			font-weight: 700;
		}

		span + span::before {
			content: "";
			display: inline-block;
			width: 1px;
			height: 14px;
			margin-right: 12px;
			background: ${C.creamLine};
			vertical-align: -2px;
		}
		${MQ.PHONE} {
			flex-direction: column;
			align-items: flex-start;
			gap: 2px;

			span + span::before {
				display: none;
			}
		}
	}

	.st_action {
		padding-left: 32px;
		border-left: 1px solid ${C.creamLine};

		${MQ.STACK} {
			grid-column: 2;
			padding-left: 0;
			border-left: 0;
		}

		${MQ.PHONE} {
			grid-column: 1;
		}
	}
`;

const initialsOf = (name) =>
	name
		.split(" ")
		.map((part) => part[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

/** Featured teacher testimonial strip, identical on Home and About. */
const SiteTestimonial = ({ className = "" }) => {
	const { quote, name, role, school } = FEATURED_TESTIMONIAL;

	return (
		<TestimonialWrapper className={className} aria-label="Teacher testimonial">
			<div className="sm_container">
				<figure className="st_card" {...revealProps("up")}>
					<span className="st_avatar" aria-hidden="true">
						{initialsOf(name)}
						<span className="st_mark">
							<IconQuote />
						</span>
					</span>
					<div className="st_body">
						<blockquote className="st_quote">“{quote}”</blockquote>
						<figcaption className="st_author">
							<span>
								<strong>{name}</strong>
							</span>
							<span>{role}</span>
							<span>{school}</span>
						</figcaption>
					</div>
					<div className="st_action">
						<SiteButton to={CTA.testimonials.to} variant="outline">
							{CTA.testimonials.label}
						</SiteButton>
					</div>
				</figure>
			</div>
		</TestimonialWrapper>
	);
};

export default SiteTestimonial;
